import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { defaultPageSize } from "helpers/utils";

function useInfiniteScroll(
	data,
	count,
	fetchData,
	page,
	searchText,
	scrollEvent = window,
	extra = {}
) {
	const [hasMore, setHasMore] = useState(true);
	const [loading, setLoading] = useState(false);
	const dataRef = useRef(data);
	const countRef = useRef(count);
	const pageRef = useRef(page);
	const prevPageRef = useRef(0);
	const extraInfo = useRef(extra);
	const searchInfo = useRef(searchText);
	const loadingRef = useRef(loading);
	const threshold = 1;

	useMemo(() => {
		dataRef.current = data;
		countRef.current = count;
		pageRef.current = page;
		extraInfo.current = extra;
		searchInfo.current = searchText;
	}, [data, count, page, extra, searchText]);

	// Keep loading ref in sync
	useEffect(() => {
		loadingRef.current = loading;
	}, [loading]);

	useEffect(() => {
		setHasMore(true);
		prevPageRef.current = page === 1 ? 0 : prevPageRef.current;
	}, [page]);

	useEffect(() => {
		setHasMore(true);
		pageRef.current = 1;
	}, [searchText]);

	const fetchMoreData = useCallback(async () => {
		if (countRef.current / pageRef.current < defaultPageSize()) {
			setHasMore(false);
			return;
		}
		if (prevPageRef.current !== pageRef.current) {
			prevPageRef.current = pageRef.current;
			setLoading(true);
			await fetchData(
				pageRef.current,
				dataRef.current,
				extraInfo.current,
				searchInfo.current
			);
			setLoading(false);
		}
	}, [fetchData]);

	const handleScroll = useCallback(
		(e, uniqueId) => {
			if (loadingRef.current) return;

			if (uniqueId) {
				const element = document.getElementById(uniqueId);
				if (
					element &&
					Math.ceil(element.offsetHeight + element.scrollTop) -
						element.scrollHeight >=
						0
				) {
					fetchMoreData();
				}
			} else if (
				document.documentElement.offsetHeight -
					(scrollEvent.innerHeight + document.documentElement.scrollTop) <=
				threshold
			) {
				fetchMoreData();
			}
		},
		[fetchMoreData, scrollEvent]
	);

	useEffect(() => {
		scrollEvent.addEventListener("scroll", handleScroll);
		return () => scrollEvent.removeEventListener("scroll", handleScroll);
	}, [scrollEvent, handleScroll]);

	const gotoTop = useCallback(() => {
		scrollEvent.scroll({ top: 0, left: 0, behavior: "smooth" });
	}, [scrollEvent]);

	return { hasMore, loading, gotoTop, handleScroll };
}

export default useInfiniteScroll;
